const ACCESS_TOKEN_KEY = "access_token";
const REFRESH_TOKEN_KEY = "refresh_token";
const AUTH_USER_KEY = "pixyquiz_auth_user";

const SHARED_ACCESS_COOKIE = "pixyquiz_shared_access_token";
const SHARED_REFRESH_COOKIE = "pixyquiz_shared_refresh_token";
const SHARED_USER_COOKIE = "pixyquiz_shared_auth_user";

function safeLocalStorageGet(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeLocalStorageSet(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch {}
}

function safeLocalStorageRemove(key) {
  try {
    window.localStorage.removeItem(key);
  } catch {}
}

function setCookie(name, value, days = 14) {
  if (typeof document === "undefined") {
    return;
  }

  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}

function getCookie(name) {
  if (typeof document === "undefined") {
    return null;
  }

  const prefix = `${name}=`;
  const raw = document.cookie
    .split(";")
    .map((item) => item.trim())
    .find((item) => item.startsWith(prefix));

  return raw ? decodeURIComponent(raw.slice(prefix.length)) : null;
}

function clearCookie(name) {
  if (typeof document === "undefined") {
    return;
  }

  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
}

function decodeJwtPayload(token) {
  try {
    const payload = token.split(".")[1];
    if (!payload) {
      return null;
    }

    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(
      normalized.length + ((4 - (normalized.length % 4)) % 4),
      "="
    );

    return JSON.parse(window.atob(padded));
  } catch {
    return null;
  }
}

function isExpiredJwt(token) {
  if (!token) {
    return true;
  }

  const payload = decodeJwtPayload(token);
  if (!payload?.exp) {
    return false;
  }

  return payload.exp * 1000 <= Date.now();
}

function firstValidToken(tokens) {
  return tokens.find((token) => token && !isExpiredJwt(token)) || null;
}

export function readSharedAuthSnapshot() {
  if (typeof window === "undefined") {
    return { access: null, refresh: null, user: null };
  }

  const access = firstValidToken([
    getCookie(SHARED_ACCESS_COOKIE),
    safeLocalStorageGet(ACCESS_TOKEN_KEY),
  ]);
  const refresh = firstValidToken([
    getCookie(SHARED_REFRESH_COOKIE),
    safeLocalStorageGet(REFRESH_TOKEN_KEY),
  ]);

  const rawUser =
    getCookie(SHARED_USER_COOKIE) || safeLocalStorageGet(AUTH_USER_KEY);

  let user = null;
  if (rawUser) {
    try {
      user = JSON.parse(rawUser);
    } catch {
      user = null;
    }
  }

  return { access, refresh, user };
}

export function persistSharedAuth({ access, refresh, user }) {
  if (typeof window === "undefined") {
    return;
  }

  if (access) {
    safeLocalStorageSet(ACCESS_TOKEN_KEY, access);
    setCookie(SHARED_ACCESS_COOKIE, access);
  }

  if (refresh) {
    safeLocalStorageSet(REFRESH_TOKEN_KEY, refresh);
    setCookie(SHARED_REFRESH_COOKIE, refresh);
  }

  if (user) {
    const serializedUser = JSON.stringify(user);
    safeLocalStorageSet(AUTH_USER_KEY, serializedUser);
    setCookie(SHARED_USER_COOKIE, serializedUser);
  }
}

export function clearSharedAuth() {
  if (typeof window === "undefined") {
    return;
  }

  safeLocalStorageRemove(ACCESS_TOKEN_KEY);
  safeLocalStorageRemove(REFRESH_TOKEN_KEY);
  safeLocalStorageRemove(AUTH_USER_KEY);
  clearCookie(SHARED_ACCESS_COOKIE);
  clearCookie(SHARED_REFRESH_COOKIE);
  clearCookie(SHARED_USER_COOKIE);
}
